import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal } from "@/components/motion/primitives";
import SEO, { breadcrumbSchema } from "@/components/site/SEO";

const DESIGNS = [
  { key: "mark", name: "Mark", desc: "The geometric A. Quiet, certain, everywhere.", img: "/shop-images/aveno-tee-mark.png" },
  { key: "wordmark", name: "Wordmark", desc: "The name, set bold. Nothing to hide behind.", img: "/shop-images/aveno-notebook-wordmark.png" },
  { key: "horizon", name: "Horizon", desc: "One line, one sun. Mornings, printed.", img: "/shop-images/aveno-hoodie-horizon.png" },
  { key: "grid", name: "Grid", desc: "Brutalist dots. Order you can feel.", img: "/shop-images/aveno-cushion-grid.png" },
  { key: "orbit", name: "Orbit", desc: "Concentric circles — everything in its place.", img: "/shop-images/aveno-cap-orbit.png" },
  { key: "wave", name: "Wave", desc: "A single flowing line. Movement, paused.", img: "/shop-images/aveno-bottle-wave.png" },
  { key: "terra", name: "Terra", desc: "Terracotta arches. Warmth, geometric.", img: "/shop-images/aveno-mug-terra.png" },
];

const COLLECTION = [
  { name: "Organic T-Shirts", img: "/shop-images/aveno-tee-mark.png", note: "220gsm organic cotton" },
  { name: "Pullover Hoodies", img: "/shop-images/aveno-hoodie-horizon.png", note: "Brushed fleece, relaxed fit" },
  { name: "Ceramic Mugs", img: "/shop-images/aveno-mug-terra.png", note: "350ml stoneware, matte glaze" },
  { name: "Steel Bottles", img: "/shop-images/aveno-bottle-wave.png", note: "24h cold / 12h hot" },
  { name: "Canvas Totes", img: "/shop-images/aveno-tote-grid.png", note: "Heavy 340gsm canvas" },
  { name: "Art Posters", img: "/shop-images/aveno-poster-orbit.png", note: "Museum-grade 200gsm matte" },
  { name: "Dad Caps", img: "/shop-images/aveno-cap-orbit.png", note: "Embroidered, adjustable" },
  { name: "Notebooks", img: "/shop-images/aveno-notebook-wordmark.png", note: "A5 dotted, lay-flat" },
];

const GIFT_SETS = [
  { name: "Desk Essentials Set", desc: "Mug, notebook and stickers in a kraft gift box — the workday starter kit.", img: "/shop-images/aveno-gift-desk.png" },
  { name: "Weekender Set", desc: "Tote, bottle and cap in a kraft gift box — for slow weekends and long walks.", img: "/shop-images/aveno-gift-weekend.png" },
];

export default function Aveno() {
  return (
    <div data-testid="aveno-page">
      <SEO
        title="AVENO — Everyday Goods. Made Properly. | Julius Mason"
        description="AVENO is Julius Mason's own goods brand: organic apparel, drinkware, stationery and home goods in seven minimal design collections."
        path="/aveno"
        lang="en"
        keywords="AVENO, everyday goods, organic t-shirts, ceramic mugs, minimal design brand"
        schema={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "AVENO", path: "/aveno" },
        ])}
      />

      {/* Hero */}
      <section className="relative" data-testid="aveno-hero">
        <div className="w-full aspect-[21/9] max-h-[70vh] overflow-hidden bg-[#FAF7F2]">
          <img src="/shop-images/aveno-hero.png" alt="AVENO everyday goods flat-lay" className="w-full h-full object-cover" />
        </div>
        <div className="mx-auto max-w-[1200px] px-6 md:px-12 -mt-16 md:-mt-24 relative z-10">
          <div className="bg-card border border-border soft-shadow-lg p-8 md:p-12 max-w-2xl">
            <img src="/shop-images/aveno-logo.png" alt="AVENO logo" className="w-16 h-16 object-contain mb-5" data-testid="aveno-hero-logo" />
            <h1 className="font-display text-4xl sm:text-5xl leading-tight mb-4">Everyday goods.<br />Made properly.</h1>
            <p className="text-muted-foreground leading-relaxed">
              AVENO is my own goods brand — the things I use at my desk, wear on weekends, and gift to
              friends. No noise, no gimmicks. Honest materials, fair prices, and one small mark.
            </p>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12 grid md:grid-cols-2 gap-12 items-start">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary mb-4">The story</p>
            <h2 className="font-display text-3xl md:text-4xl mb-6">Why I started AVENO</h2>
            <div className="space-y-4 text-muted-foreground leading-relaxed">
              <p>
                I spend my days building websites for other people's businesses. AVENO is what I build
                for mine — a place for products I'd actually buy: a mug that feels right at 7am, a
                notebook that lies flat, a shirt that survives a hundred washes.
              </p>
              <p>
                Every design starts from the same mark — the geometric A — and the same palette:
                burnt orange, warm cream, charcoal. If it doesn't earn the mark, it doesn't ship.
              </p>
            </div>
          </Reveal>
          <Reveal>
            <div className="bg-[#FAF7F2] border border-border p-10 md:p-14 flex flex-col items-center text-center">
              <img src="/shop-images/aveno-social.png" alt="AVENO wordmark and tagline" className="w-full max-w-sm object-contain" data-testid="aveno-wordmark" />
              <p className="mt-6 text-xs text-muted-foreground font-mono uppercase tracking-[0.25em]">Est. 2026 · Designed in France</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Design collections */}
      <section className="py-16 md:py-24 border-t border-border bg-secondary/40" data-testid="aveno-designs">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary mb-4">The designs</p>
            <h2 className="font-display text-3xl md:text-4xl mb-3">Seven marks. One language.</h2>
            <p className="text-muted-foreground max-w-2xl mb-12">
              Every AVENO product carries one of seven designs. Same palette, same restraint — collect
              your favourite across the whole range.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {DESIGNS.map((d) => (
              <Reveal key={d.key}>
                <div className="group" data-testid="aveno-design-card">
                  <div className="aspect-square overflow-hidden bg-[#FAF7F2] border border-border mb-3">
                    <img src={d.img} alt={`AVENO ${d.name} design`} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <p className="font-semibold text-sm">{d.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{d.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Collection highlights */}
      <section className="py-16 md:py-24 border-t border-border" data-testid="aveno-collection">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary mb-4">The range</p>
            <h2 className="font-display text-3xl md:text-4xl mb-12">Wear it. Pour it. Carry it.</h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {COLLECTION.map((c) => (
              <Reveal key={c.name}>
                <Link to="/shop" className="group block" data-testid="aveno-collection-card">
                  <div className="aspect-[4/5] overflow-hidden bg-[#FAF7F2] border border-border mb-3">
                    <img src={c.img} alt={`AVENO ${c.name}`} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <p className="font-semibold text-sm group-hover:text-primary transition-colors">{c.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{c.note}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Kids & Baby */}
      <section className="py-16 md:py-24 border-t border-border" data-testid="aveno-kids">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary mb-4">Kids & Baby</p>
            <h2 className="font-display text-3xl md:text-4xl mb-3">The little line, modeled by my little ones.</h2>
            <p className="text-muted-foreground max-w-2xl mb-12">
              Mini versions of the grown-up range — onesies, tees, hoodies and more, in the same seven
              designs. Modeled at home by my son and daughters.
            </p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { name: "Baby Onesies", img: "/shop-images/aveno-baby-onesie-mark.png", note: "Organic cotton, 0-24m" },
              { name: "Kids T-Shirts", img: "/shop-images/aveno-kids-tee-mark.png", note: "Tagless, ages 2-10" },
              { name: "Kids Hoodies", img: "/shop-images/aveno-kids-hoodie-terra.png", note: "Brushed fleece, ages 2-10" },
              { name: "Kids Backpacks", img: "/shop-images/aveno-kids-backpack-horizon.png", note: "Lightweight, ages 3-10" },
            ].map((c) => (
              <Reveal key={c.name}>
                <Link to="/shop" className="group block" data-testid="aveno-kids-card">
                  <div className="aspect-[4/5] overflow-hidden bg-[#FAF7F2] border border-border mb-3">
                    <img src={c.img} alt={`AVENO ${c.name}`} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <p className="font-semibold text-sm group-hover:text-primary transition-colors">{c.name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{c.note}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Gift sets */}
      <section className="py-16 md:py-24 border-t border-border bg-secondary/40" data-testid="aveno-gifts">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary mb-4">Gift sets</p>
            <h2 className="font-display text-3xl md:text-4xl mb-12">Boxed and ready to give.</h2>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-6">
            {GIFT_SETS.map((g) => (
              <Reveal key={g.name}>
                <div className="bg-card border border-border overflow-hidden group" data-testid="aveno-gift-card">
                  <div className="aspect-[16/9] overflow-hidden">
                    <img src={g.img} alt={`AVENO ${g.name}`} loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <div className="p-6">
                    <p className="font-display text-xl mb-2">{g.name}</p>
                    <p className="text-sm text-muted-foreground">{g.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Packaging */}
      <section className="py-16 md:py-24 border-t border-border">
        <div className="mx-auto max-w-[1200px] px-6 md:px-12 grid md:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="aspect-[4/3] overflow-hidden bg-[#FAF7F2] border border-border">
              <img src="/shop-images/aveno-packaging.png" alt="AVENO kraft packaging with thank-you card" loading="lazy" className="w-full h-full object-cover" />
            </div>
          </Reveal>
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-primary mb-4">The details</p>
            <h2 className="font-display text-3xl md:text-4xl mb-6">Shipped with care</h2>
            <p className="text-muted-foreground leading-relaxed mb-8">
              Every order leaves in kraft packaging with the mark on the box and a thank-you card inside.
              Thirty-day easy returns, no questions. Secure checkout powered by Shopify.
            </p>
            <Link to="/shop" data-testid="aveno-shop-cta"
              className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-7 py-3.5 rounded-full hover:bg-primary/90 active:scale-95 transition">
              Browse the collection <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
